import * as React from 'react';
import { styled, type StyledComponentWithRef } from '@glitz/react';
import { useFeedback, Status, type State, Behavior, type PushOptions } from '@avensia/excite-ui';
import { Theme, type ThemeType } from '../../theme';
import { Button, type PropType as ButtonPropType } from './button';
import { ScaleAnimation } from '@avensia/excite-ui';
import { Spinner, CheckIcon, Alert } from '../icons';
import { NnTestIdProps } from '../../shared/test-ids';

export { useFeedback, Behavior } from '@avensia/excite-ui';

type FeedbackStatePropType = {
  state: State;
};

type ThemeButtonPropType = ButtonPropType & {
  fulfilledTheme?: ThemeType;
  rejectedTheme?: ThemeType;
};

export function createFeedbackControlledButton(): StyledComponentWithRef<
  React.ButtonHTMLAttributes<HTMLButtonElement> & FeedbackStatePropType & ThemeButtonPropType,
  HTMLButtonElement
>;
export function createFeedbackControlledButton<TProps extends ThemeButtonPropType, TElement extends HTMLElement>(
  Component: StyledComponentWithRef<TProps, TElement>,
): StyledComponentWithRef<
  React.ButtonHTMLAttributes<HTMLButtonElement> & FeedbackStatePropType & TProps,
  HTMLButtonElement
>;
export function createFeedbackControlledButton<TProps extends ThemeButtonPropType, TElement extends HTMLElement>(
  Component: StyledComponentWithRef<TProps, TElement> = Button as any,
) {
  return styled(
    React.forwardRef<TElement, React.PropsWithChildren<FeedbackStatePropType> & TProps>(
      ({ state, as, children, disabled, fulfilledTheme, rejectedTheme, ...restProps }, ref) => {
        const isDefault = state.status === Status.Default;
        const isPending = state.status === Status.Pending;
        const isFulfilled = state.status === Status.Fulfilled;
        const isRejected = state.status === Status.Rejected;

        if (isRejected) {
          as = as === 'transparent' && !rejectedTheme ? Theme.Transparent : (rejectedTheme ?? Theme.Negative);
        } else if (isFulfilled) {
          as = as === 'transparent' && !fulfilledTheme ? Theme.Transparent : (fulfilledTheme ?? Theme.Positive);
        }

        return (
          <Component {...(restProps as any)} disabled={state.disabled || disabled} as={as} ref={ref}>
            <Text visible={isDefault} keepElement>
              {children}
            </Text>
            <Layer visible={isFulfilled} {...NnTestIdProps.CheckoutVoucherSubmitButtonFulfilled}>
              <CheckIcon />
            </Layer>
            <Layer visible={isRejected} {...NnTestIdProps.CheckoutVoucherSubmitButtonRejected}>
              <Alert />
            </Layer>
            <Layer visible={isPending}>
              <Spinner />
            </Layer>
          </Component>
        );
      },
    ),
    {
      display: 'flex',
      position: 'relative',
    },
  );
}

export const FeedbackControlledButton = createFeedbackControlledButton(Button);

const Text = styled(ScaleAnimation, {
  // Fixes alignment
  display: 'inherit',
  flexDirection: 'inherit',
  alignItems: 'inherit',
  justifyContent: 'inherit',
  width: 'inherit',
});

const Layer = styled(Text, {
  display: 'flex', // Aligns content properly
  position: 'absolute',
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

export type FeedbackButtonPropType = PushOptions &
  ButtonPropType &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
    behavior?: Behavior;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<any>;
    fulfilledTheme?: ThemeType;
    rejectedTheme?: ThemeType;
  };

export function createFeedbackStandaloneButton(Component = Button) {
  const Controlled = createFeedbackControlledButton(Component);

  return styled(
    React.forwardRef(
      (
        { minimumPending, maximumFulfilled, maximumRejected, behavior, onClick, ...restProps }: FeedbackButtonPropType,
        ref: React.Ref<HTMLButtonElement>,
      ) => {
        const [state, push] = useFeedback({
          defaultMinimumPending: minimumPending,
          defaultMaximumFulfilled: maximumFulfilled,
          defaultMaximumRejected: maximumRejected,
          behavior,
        });

        const pushWhenPromise = React.useCallback(
          (e: React.MouseEvent<HTMLButtonElement>) => {
            if (restProps.disabled || restProps['aria-disabled']) {
              e.preventDefault();
              return;
            }
            const value = onClick(e);
            if (value instanceof Promise) {
              push(value);
            }
          },
          [onClick, push, restProps],
        );

        return <Controlled state={state} {...restProps} onClick={pushWhenPromise} ref={ref} />;
      },
    ),
  );
}

export const FeedbackStandaloneButton = createFeedbackStandaloneButton();
